import { DateTime } from '@cheqroom/date-time';

import { type DynamicFilterValue, parseDynamicFilters } from '../../filters';

/**
 * Spotcheck list / search status values accepted by {@link buildSpotcheckSearchFilters}.
 * Values match `spotcheck-filters:status.options.*` i18n keys; mapped to Python list names below.
 */
export enum SpotcheckStatusFilter {
	IN_PROGRESS = 'in_progress',
	FINISHED = 'finished',
	FINISHED_WITH_ISSUES = 'finished_with_issues',
	FINISHED_WITHOUT_ISSUES = 'finished_without_issues',
	ARCHIVED = 'archived',
}

const statusToListNameMap: Record<string, string> = {
	[SpotcheckStatusFilter.IN_PROGRESS]: 'open',
	[SpotcheckStatusFilter.FINISHED]: 'closed',
	[SpotcheckStatusFilter.FINISHED_WITH_ISSUES]: 'closed_issues',
	[SpotcheckStatusFilter.FINISHED_WITHOUT_ISSUES]: 'closed_success',
	[SpotcheckStatusFilter.ARCHIVED]: 'archived',
};

export type SpotcheckListName = [string, 'or' | 'and'];

export type SpotcheckSearchFiltersQuery = {
	listNames?: SpotcheckListName[];
	archived?: string | null;
	kind__in?: string[];
	users__in?: string[];
	assignees__in?: string[];
	created__gte?: string;
	closed__lte?: string;
	[key: string]: unknown;
};

export type BuildSpotcheckSearchFiltersInput = {
	statuses?: string[];
	kindIds?: string[];
	participantIds?: string[];
	assigneeIds?: string[];
	dateRange?: {
		start?: string;
		end?: string;
	};
	/** Extra field filters keyed by field id. */
	dynamicFilters?: Record<string, DynamicFilterValue | undefined>;
};

/**
 * Builds Python spotcheck search / `customFieldFilters` params from neutral filter ids.
 * UI adapters should unwrap chip shapes before calling this.
 */
export const buildSpotcheckSearchFilters = (
	input: BuildSpotcheckSearchFiltersInput = {}
): SpotcheckSearchFiltersQuery => {
	const { statuses, kindIds, participantIds, assigneeIds, dateRange, dynamicFilters = {} } = input;

	const query: SpotcheckSearchFiltersQuery = {
		archived: null,
	};

	const hasStatuses = Boolean(statuses && statuses.length > 0);

	if (hasStatuses && statuses) {
		const listNames: SpotcheckListName[] = [];

		for (const status of statuses) {
			const listName = statusToListNameMap[status];
			if (listName) {
				listNames.push([listName, 'or']);
			}
		}

		if (listNames.length > 0) {
			query.listNames = listNames;
		}

		if (statuses.includes(SpotcheckStatusFilter.ARCHIVED)) {
			delete query.archived;
		}
	}

	if (kindIds && kindIds.length > 0) {
		query.kind__in = kindIds;
	}

	if (participantIds && participantIds.length > 0) {
		query.users__in = participantIds;
	}

	if (assigneeIds && assigneeIds.length > 0) {
		query.assignees__in = assigneeIds;
	}

	if (dateRange) {
		const { start, end } = dateRange;

		if (start) {
			query.created__gte = DateTime.fromISOString(start).startOfDay().toFloatingISOString();
		}
		if (end) {
			query.closed__lte = DateTime.fromISOString(end).endOfDay().toFloatingISOString();
		}
	}

	return {
		...query,
		...parseDynamicFilters(dynamicFilters),
	};
};
